/**
 * @file CreditStockDialog.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 *
 * Saving a customer credit note with stock-tracked lines (Cam, 15 Oct 2026):
 * does each line put the goods back into stock, or only correct the price?
 *
 *   Goods returned    the item code goes to Xero — the goods come back into
 *                     stock there.
 *   Price correction  no item code goes to Xero (the account only, the code
 *                     in the words) — stock is not changed.
 *
 * Both are always offered. Each line shows its quantity against the invoice
 * line it credits and starts on what the QUANTITY suggests
 * (utils/creditStockEffect.assumeCreditKind — prices play no part): the same
 * quantity as the invoice → price correction; fewer → goods returned. A line
 * with no suggestion (more than invoiced, no matching line, no invoice to
 * compare with) must be chosen before Save is allowed — the server never
 * chooses either (409 CREDIT_STOCK_KIND_REQUIRED). Before
 * migrations/credit_note_line_stock.sql has run there is nothing to choose:
 * the dialog says every line goes as goods returned, as before.
 *
 * 1.1.0 (2026-10-15): `mode="allocate"` — allocating an EXISTING credit
 * (AllocateCreditNoteModal) asks the same, against the invoice(s) being
 * allocated to (GET /api/invoices/:id/credit-stock/allocation). Choices are
 * keyed by line id; each line starts on the kind stored at save, with the
 * invoice's suggestion said beside it when it differs. A choice that differs
 * from what the credit does now (a line stored with none counts as goods
 * returned) is said: the credit is changed first, then allocated — with a
 * reason on a posted credit (prefilled), or greyed with why when the credit
 * cannot be changed (keep its saved effect to allocate).
 *
 * The shared ConfirmDialog, Confirm greyed (`confirmDisabled`) until every
 * line is chosen.
 */

import React from 'react';
import ConfirmDialog from './ConfirmDialog';
import Dropdown from './Dropdown';
import Input from './Input';
import './CreditStockDialog.css';
import {
  GOODS_RETURNED, PRICE_CORRECTION, effectText, comparisonText, rowsWithoutKind,
  allocationChoices, kindWords, changeText
} from '../utils/creditStockEffect';

const keyOf = (mode, r) => (mode === 'allocate' ? r.line_id : r.index);

function RowNote({ mode, row }) {
  if (mode === 'allocate' && row.stored) {
    const differs = row.assumed && row.assumed !== row.stored;
    return (
      <div className="credit-stock-sub">
        Saved as {kindWords(row.stored)}
        {differs ? ` — suggested here: ${kindWords(row.assumed)} (${row.reason})` : ''}
      </div>
    );
  }
  return row.assumed
    ? <div className="credit-stock-sub">Suggested: {kindWords(row.assumed)} — {row.reason}</div>
    : <div className="credit-stock-sub warn">No suggestion — {row.reason}. Choose one.</div>;
}

function CreditStockBody({ mode, plan, choices, onChoose, reason, onReasonChange }) {
  const rows = plan?.rows || [];
  const invoices = (plan?.invoices || []).map(i => i.invoice_no).filter(Boolean);
  const creditNo = plan?.credit?.invoice_no || 'this credit note';
  const allocate = mode === 'allocate';
  const changes = allocate ? allocationChoices(rows, choices).changes : [];
  const block = allocate ? plan?.change?.block : null;
  return (
    <div className="credit-stock-body">
      {plan?.ready === false ? (
        <p className="credit-stock-hint">
          <span className="material-symbols-rounded">info</span>
          These lines will be sent as goods returned — back into stock in Xero — as every credit note has been.
          The database update that lets you choose (migrations/credit_note_line_stock.sql) has not been run yet.
        </p>
      ) : (
        <p className="credit-stock-intro">
          {allocate
            ? `Before ${creditNo} is allocated: say for each stock item whether the goods came back or only the price is being corrected.`
            : 'Say for each stock item whether the goods came back or only the price is being corrected.'}
          {invoices.length
            ? ` Compared with ${invoices.join(', ')}${allocate ? ' (being allocated to)' : ''}.`
            : ' There is no invoice to compare with, so choose each one.'}
        </p>
      )}
      <div className="table-container">
        <table className="data-table credit-stock-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>This credit (and the invoice)</th>
              <th>In Xero</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const key = keyOf(mode, r);
              const kind = plan?.ready === false ? GOODS_RETURNED : (choices[key] !== undefined ? choices[key] : r.kind);
              return (
                <tr key={key} className={kind ? '' : 'needs-choice'}>
                  <td>
                    <div className="credit-stock-product">{r.item_code ? `${r.item_code} — ` : ''}{r.description}</div>
                    <RowNote mode={mode} row={r} />
                  </td>
                  <td className="credit-stock-compare">{comparisonText(r)}</td>
                  <td className="credit-stock-choice">
                    {plan?.ready === false ? (
                      <span>{effectText(GOODS_RETURNED, { quantity: r.quantity, unitWeightKg: r.unit_weight_kg })}</span>
                    ) : (
                      <Dropdown
                        value={kind || ''}
                        placeholder="Choose…"
                        escapeOverflow
                        onChange={(v) => onChoose(key, v)}
                        options={[
                          { value: PRICE_CORRECTION, label: effectText(PRICE_CORRECTION) },
                          { value: GOODS_RETURNED, label: effectText(GOODS_RETURNED, { quantity: r.quantity, unitWeightKg: r.unit_weight_kg }) }
                        ]}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {allocate && changes.length > 0 && (
        <div className={`credit-stock-hint${block ? ' error' : ''}`}>
          <span className="material-symbols-rounded">{block ? 'block' : 'edit_note'}</span>
          <div className="credit-stock-change">
            {block ? (
              <span>
                {creditNo} can't be changed here ({String(block).replace(/\.\s*$/, '')}), so {changes.map(changeText).join('; ')} is
                not possible. Keep its saved effect to allocate it, or go back.
              </span>
            ) : (
              <span>
                {creditNo} will be changed first ({changes.map(changeText).join('; ')}), then allocated.
                {plan?.change?.in_xero
                  ? ` In Xero the item code is ${changes.every(c => c.to === PRICE_CORRECTION) ? 'taken off' : changes.every(c => c.to === GOODS_RETURNED) ? 'put back on' : 'taken off or put back on'} its line.`
                  : ''}
                {plan?.change?.allocated ? ' Its existing allocations come off in Xero and go back on around the change.' : ''}
              </span>
            )}
            {!block && plan?.change?.needs_reason && (
              <label className="credit-stock-reason">
                <span>Why (kept in its history here and in Xero)</span>
                <Input
                  value={reason || ''}
                  onChange={(e) => onReasonChange && onReasonChange(e.target.value)}
                  placeholder={plan?.change?.default_reason || 'Reason for the change'}
                  maxLength={200}
                />
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object}   props
 * @param {boolean}  props.isOpen
 * @param {object}   props.plan       the server's rows (credit-stock preview, or the allocation's)
 * @param {object}   props.choices    line index (save) / line id (allocate) → kind chosen here
 * @param {Function} props.onChoose   (key, kind)
 * @param {Function} props.onConfirm  save / allocate with the choices
 * @param {Function} props.onCancel   back
 * @param {'save'|'allocate'} [props.mode]
 * @param {string}   [props.reason]          allocate: the edit's reason (a posted credit)
 * @param {Function} [props.onReasonChange]  allocate
 */
function CreditStockDialog({ isOpen, plan, choices = {}, onChoose, onConfirm, onCancel, mode = 'save', reason = '', onReasonChange }) {
  const allocate = mode === 'allocate';
  const rows = plan?.rows || [];
  let missing = [];
  let disabledReason = null;
  if (plan?.ready !== false) {
    if (allocate) {
      const out = allocationChoices(rows, choices);
      missing = out.missing;
      if (!missing.length && out.changes.length && plan?.change?.block) {
        disabledReason = `${plan?.credit?.invoice_no || 'The credit note'} can't be changed here — keep its saved stock effect to allocate`;
      } else if (!missing.length && out.changes.length && plan?.change?.needs_reason && !String(reason || '').trim()) {
        disabledReason = 'Say why the stock effect is being changed';
      }
    } else {
      missing = rowsWithoutKind(rows, choices);
    }
  }
  if (missing.length) {
    disabledReason = `Choose goods returned or price correction for ${missing.map(r => r.item_code || r.description).join(', ')} first`;
  }
  return (
    <ConfirmDialog
      isOpen={isOpen}
      type="info"
      className="credit-stock-dialog"
      title={allocate ? `Stock on ${plan?.credit?.invoice_no || 'this credit note'}` : 'Stock on this credit note'}
      message={<CreditStockBody mode={mode} plan={plan} choices={choices} onChoose={onChoose} reason={reason} onReasonChange={onReasonChange} />}
      confirmText={allocate ? 'Allocate credit' : 'Save credit note'}
      confirmIcon={allocate ? 'credit_score' : 'save'}
      cancelText={allocate ? 'Back to the allocation' : 'Back to the credit note'}
      confirmDisabled={!!disabledReason}
      confirmDisabledReason={disabledReason}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

export default CreditStockDialog;
